/** The two live-capture media kinds that get generated filenames. */
export type CaptureKind = "photo" | "memo";

/**
 * Build a readable capture filename from the local wall clock:
 * `<prefix>-YYYYMMDD-HHMMSS.<extension>`, e.g. `photo-20261031-143005.jpg`.
 *
 * Collisions within the same capture batch are resolved deterministically by
 * appending `-2`, `-3`, ... before the extension, so two captures in the same
 * second never share a name. Deduplication compares stems, because the API
 * stores names without their extension.
 *
 * @param kind media kind; selects the prefix and extension.
 * @param at capture time; read in local time.
 * @param usedNames names already taken in the batch, with or without extension.
 * @returns a name whose stem is not present in `usedNames`.
 */
export function captureFileName(
  kind: CaptureKind,
  at: Date,
  usedNames: readonly string[] = [],
): string {
  const { prefix, extension } = CAPTURE_FILES[kind];
  const base = `${prefix}-${timestamp(at)}`;
  const taken = new Set(usedNames.map(stem));
  let name = `${base}.${extension}`;
  for (let suffix = 2; taken.has(stem(name)); suffix++) {
    name = `${base}-${suffix}.${extension}`;
  }
  return name;
}

/** Derive the display names from a list of files or capture objects. */
export function fileNames(files: readonly { name: string }[]): string[];
export function fileNames(captures: readonly { file: { name: string } }[]): string[];
export function fileNames(items: readonly ({ name: string } | { file: { name: string } })[]): string[] {
  return items.map((item) => ("name" in item ? item.name : item.file.name));
}

const CAPTURE_FILES: Record<CaptureKind, { prefix: string; extension: string }> = {
  photo: { prefix: "photo", extension: "jpg" },
  memo: { prefix: "memo", extension: "webm" },
};

const pad = (value: number) => String(value).padStart(2, "0");

/** Local wall-clock stamp used in capture names: `YYYYMMDD-HHMMSS`. */
function timestamp(at: Date): string {
  return (
    `${at.getFullYear()}${pad(at.getMonth() + 1)}${pad(at.getDate())}` +
    `-${pad(at.getHours())}${pad(at.getMinutes())}${pad(at.getSeconds())}`
  );
}

/** The name without its final extension, matching what the API stores. */
function stem(name: string): string {
  return name.replace(/\.[^.]+$/, "");
}
